import React from "react";
import { RichTextEditor } from "@/components/ui/rich-text-editor";
import { RichTextDisplay } from "@/components/ui/rich-text-display";
import { ContentBlock } from "@/types/case-study";
import { cleanWavyUnderlines } from "@/utils/cleanWavyUnderlines";

interface TextBlockProps {
  block: ContentBlock;
  onUpdate: (content: any) => void;
  onRemove?: () => void;
  isActive?: boolean;
  isRemovable?: boolean;
}

export const TextBlock: React.FC<TextBlockProps> = ({
  block,
  onUpdate,
  onRemove,
  isActive = false,
  isRemovable = true,
}) => {
  // Extract text from content object (content shape: { text: string })
  const textContent = typeof block.content === 'string'
    ? block.content
    : (block.content as { text?: string })?.text || "";

  const handleContentChange = (content: string) => {
    // Clean wavy underlines before saving to database
    const cleanedContent = cleanWavyUnderlines(content);
    // Update with the object format { text: string }
    onUpdate({ text: cleanedContent });
  };

  return (
    <div className={`group relative ${isActive ? 'ring-2 ring-blue-500 ring-opacity-50' : ''} ${isActive && isRemovable ? 'hover:ring-1 hover:ring-gray-300' : ''}`}>
      <div className="min-h-[100px]">
        {isActive ? (
          <RichTextEditor
            value={textContent}
            onChange={handleContentChange}
            placeholder="Add your text content here..."
          />
        ) : (
          <RichTextDisplay
            content={textContent}
            className="prose prose-sm max-w-none text-gray-700 [&_ul]:space-y-1 [&_ol]:space-y-1 [&_li]:leading-relaxed [&_li_p]:my-0 [&_li_p]:mt-0 [&_li_p]:mb-0"
            fallbackText="No content added yet. Start writing to see your content here."
          />
        )}
      </div>
      {isActive && isRemovable && onRemove && (
        <button
          onClick={onRemove}
          className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-70 hover:opacity-100 transition-opacity hover:bg-red-600"
          title="Delete this content block"
        >
          ×
        </button>
      )}
    </div>
  );
};

